import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

import { useAuthStore } from "@/features/auth/store";
import { apiErrorMessage } from "@/lib/api/errors";
import { useOfflineCreate, type OfflineCreateVars } from "@/lib/api/offline-mutations";
import { useMediaQuery } from "@/lib/hooks/use-media-query";
import { useIsUiTemplateE } from "@/lib/hooks/use-ui-template";
import { showsGeneralClinical } from "@/lib/permissions/can";

import { patientsApi } from "./api";
import { usePatientsListViewStore, type PatientsListView } from "./store";
import type { Patient, PatientFilters } from "./types";

const KEY = "patients";

/** Clé de mutation de création — partagée avec le registre offline. */
export const PATIENTS_CREATE_KEY = [KEY, "create"] as const;

export function usePatients(filters: PatientFilters = {}) {
  return useQuery({
    queryKey: [KEY, "list", filters],
    queryFn: () => patientsApi.list(filters),
    placeholderData: (prev) => prev,
  });
}

export function usePatient(id: string | undefined) {
  return useQuery({
    queryKey: [KEY, "detail", id],
    queryFn: () => patientsApi.get(id!),
    enabled: !!id,
  });
}

export function usePatientSummary(id: string | undefined) {
  return useQuery({
    queryKey: [KEY, "summary", id],
    queryFn: () => patientsApi.summary(id!),
    enabled: !!id,
  });
}

/** Dernier matricule attribué + prochain (pour le champ N° Fiche). */
export function useNextPatientCode(enabled = true) {
  return useQuery({
    queryKey: [KEY, "next-code"],
    queryFn: () => patientsApi.nextCode(),
    enabled,
    staleTime: 0,
  });
}

export function useCreatePatient() {
  const qc = useQueryClient();
  const mutation = useMutation<Patient, unknown, OfflineCreateVars<Partial<Patient>>>({
    mutationKey: [...PATIENTS_CREATE_KEY],
    mutationFn: ({ payload, idempotencyKey }) => patientsApi.create(payload, idempotencyKey),
    onSuccess: async (created) => {
      // Pré-alimente le cache detail : le combobox affiche le label immédiatement
      // (sélection inline) sans attendre un refetch.
      qc.setQueryData([KEY, "detail", created.id], created);
      await qc.invalidateQueries({ queryKey: [KEY, "list"] });
      toast.success(`Patient ${created.patient_code} créé.`);
    },
    // Erreurs gérées par le formulaire (affichage sous le champ concerné).
  });
  return useOfflineCreate(mutation);
}

export function useUpdatePatient(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Parameters<typeof patientsApi.update>[1]) =>
      patientsApi.update(id, payload),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: [KEY, "list"] });
      await qc.invalidateQueries({ queryKey: [KEY, "detail", id] });
      toast.success("Patient mis à jour.");
    },
    // Erreurs gérées par le formulaire (affichage sous le champ concerné).
  });
}

/**
 * Suppression d'une fiche. Le serveur libère son N° dans le même geste : le
 * toast le nomme, faute de quoi rien ne signalerait que le N° est de nouveau
 * saisissable. `releasedCode` vide = N° déjà libéré (fiche resupprimée).
 */
export function useDeletePatient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string; releasedCode?: string }) => patientsApi.remove(id),
    onSuccess: async (_data, { releasedCode }) => {
      await qc.invalidateQueries({ queryKey: [KEY, "list"] });
      toast.success(
        releasedCode
          ? `Patient supprimé. N° ${releasedCode} libéré : il peut être ressaisi.`
          : "Patient supprimé.",
      );
    },
    onError: (err) => {
      toast.error(apiErrorMessage(err, "Erreur à la suppression du patient."));
    },
  });
}

export function useTransferTargets(id: string, enabled: boolean) {
  return useQuery({
    queryKey: [KEY, id, "transfer-targets"],
    queryFn: () => patientsApi.transferTargets(id),
    enabled,
  });
}

export function useTransferPatient(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (organisationId: string) => patientsApi.transfer(id, organisationId),
    onSuccess: async (patient) => {
      await qc.invalidateQueries({ queryKey: [KEY] });
      toast.success(`Fiche transférée vers ${patient.organisation_name}.`);
    },
    onError: (err) => {
      toast.error(apiErrorMessage(err, "Erreur au transfert de la fiche."));
    },
  });
}

export function useReleasePatientCode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => patientsApi.releaseCode(id),
    onSuccess: async (data) => {
      await qc.invalidateQueries({ queryKey: [KEY] });
      toast.success(`N° ${data.released_code} libéré. Il peut être ressaisi.`);
    },
    onError: (err) => {
      toast.error(apiErrorMessage(err, "Erreur à la libération du N° de fiche."));
    },
  });
}

export function usePurgePatient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, confirmFullName }: { id: string; confirmFullName: string }) =>
      patientsApi.purge(id, confirmFullName),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: [KEY] });
      toast.success("Fiche supprimée définitivement.");
    },
    onError: (err) => {
      toast.error(apiErrorMessage(err, "Erreur à la suppression définitive."));
    },
  });
}

/**
 * « Tout restaurer » : sort de la corbeille toutes les fiches du périmètre
 * affiché, d'un seul appel. Geste réversible — chaque fiche peut être remise à
 * la corbeille.
 */
export function useRestoreAllPatients() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (filters: PatientFilters) => patientsApi.restoreAll(filters),
    onSuccess: async ({ restored }) => {
      await qc.invalidateQueries({ queryKey: [KEY] });
      if (restored === 0) {
        toast.info("Aucune fiche à restaurer.");
        return;
      }
      toast.success(`${restored} fiche${restored > 1 ? "s" : ""} restaurée${restored > 1 ? "s" : ""}.`);
    },
    onError: (err) => {
      toast.error(apiErrorMessage(err, "Erreur à la restauration des fiches."));
    },
  });
}

/**
 * « Tout libérer » : rend saisissables les N° de toutes les fiches du périmètre
 * affiché. Les N° déjà libérés sont passés — d'où le compteur `skipped`.
 */
export function useReleaseAllPatientCodes() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (filters: PatientFilters) => patientsApi.releaseAllCodes(filters),
    onSuccess: async ({ released, skipped }) => {
      await qc.invalidateQueries({ queryKey: [KEY] });
      if (released.length === 0) {
        toast.info(
          skipped > 0 ? "Ces N° avaient déjà été libérés." : "Aucun N° à libérer.",
        );
        return;
      }
      const n = released.length;
      toast.success(
        `${n} N° libéré${n > 1 ? "s" : ""}. Ils peuvent être ressaisis.` +
          (skipped > 0 ? ` ${skipped} déjà libéré${skipped > 1 ? "s" : ""}.` : ""),
      );
    },
    onError: (err) => {
      toast.error(apiErrorMessage(err, "Erreur à la libération des N° de fiche."));
    },
  });
}

/**
 * « Vider la corbeille » : efface définitivement les fiches du périmètre
 * affiché. IRRÉVERSIBLE. Les fiches qui portent des actes restent en corbeille
 * — d'où le compteur `kept`.
 */
export function useEmptyTrash() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ filters, confirm }: { filters: PatientFilters; confirm: string }) =>
      patientsApi.purgeAll(filters, confirm),
    onSuccess: async ({ purged, kept }) => {
      await qc.invalidateQueries({ queryKey: [KEY] });
      const keptNote =
        kept > 0
          ? ` ${kept} fiche${kept > 1 ? "s" : ""} conservée${kept > 1 ? "s" : ""} : ${kept > 1 ? "elles portent" : "elle porte"} des actes.`
          : "";
      if (purged === 0) {
        toast.info((kept > 0 ? "Aucune fiche supprimée." : "La corbeille est déjà vide.") + keptNote);
        return;
      }
      toast.success(
        `${purged} fiche${purged > 1 ? "s" : ""} supprimée${purged > 1 ? "s" : ""} définitivement.` +
          keptNote,
      );
    },
    onError: (err) => {
      toast.error(apiErrorMessage(err, "Erreur en vidant la corbeille."));
    },
  });
}

export function useRestorePatient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => patientsApi.restore(id),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: [KEY, "list"] });
      toast.success("Patient restauré.");
    },
    onError: (err) => {
      toast.error(apiErrorMessage(err, "Erreur à la restauration du patient."));
    },
  });
}

/** Dossier médical du patient (antécédents, allergies, traitements). */
export function usePatientMedicalRecord(id: string | undefined) {
  return useQuery({
    queryKey: [KEY, "medical-record", id],
    queryFn: () => patientsApi.getMedicalRecord(id!),
    enabled: !!id,
  });
}

export function useUpsertMedicalRecord(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Parameters<typeof patientsApi.updateMedicalRecord>[1]) =>
      patientsApi.updateMedicalRecord(id, payload),
    onSuccess: async (saved) => {
      qc.setQueryData([KEY, "medical-record", id], saved);
      toast.success("Dossier médical enregistré.");
    },
    onError: (err) => {
      toast.error(apiErrorMessage(err, "Erreur à l'enregistrement du dossier médical."));
    },
  });
}

export function usePatientDuplicates(id: string | undefined) {
  return useQuery({
    queryKey: [KEY, "duplicates", id],
    queryFn: () => patientsApi.duplicates(id!),
    enabled: !!id,
  });
}

/** Fusionne un doublon (source) DANS la fiche courante conservée (targetId). */
export function useMergeIntoPatient(targetId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sourceId: string) => patientsApi.merge(sourceId, targetId),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: [KEY] });
      toast.success(
        "Doublon fusionné. L'historique a été rattaché à cette fiche ; le N° libéré est annoncé dans les notifications.",
      );
    },
    onError: (err) => {
      toast.error(apiErrorMessage(err, "Erreur à la fusion des patients."));
    },
  });
}

export function useAnonymizePatient(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (fullName: string) => patientsApi.anonymize(id, fullName),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: [KEY, "list"] });
      await qc.invalidateQueries({ queryKey: [KEY, "detail", id] });
      toast.success("Patient anonymisé. Action enregistrée dans l'audit.");
    },
    onError: (err) => {
      toast.error(apiErrorMessage(err, "La confirmation ne correspond pas au nom complet du patient."));
    },
  });
}

/**
 * Liste patients sous la présentation E (ADR 0068) : bascule Tableau |
 * Guichet, sélection partagée entre les deux vues, patient ouvert dans
 * l'aperçu. Sans le module, `enabled` est faux et la page reste celle
 * d'aujourd'hui (pas de bascule, pas de cases, clic = ouverture du dossier).
 *
 * - Sous 640 px : pas de bascule, liste seule (vue Tableau).
 * - Sous 1024 px : le Guichet montre la liste ; l'aperçu s'ouvre en plein
 *   écran au choix d'un patient (`closePreview` = « Retour à la liste »).
 * - La sélection porte sur la page affichée : elle repart de zéro quand
 *   `resetKey` (filtres, page, corbeille) change.
 */
export function usePatientsListLayout({
  rows,
  resetKey,
  allowed = true,
}: {
  rows: Patient[];
  resetKey: string;
  /** Faux en corbeille : ni Guichet ni cases. */
  allowed?: boolean;
}) {
  const templateE = useIsUiTemplateE();
  const enabled = templateE && allowed;
  const wide = useMediaQuery("(min-width: 640px)");
  const desktop = useMediaQuery("(min-width: 1024px)");
  const storedView = usePatientsListViewStore((s) => s.view);
  const setView = usePatientsListViewStore((s) => s.setView);

  const canToggle = enabled && wide;
  const view: PatientsListView = canToggle ? storedView : "table";

  const [selected, setSelected] = useState<ReadonlySet<string>>(() => new Set());
  const [activeId, setActiveId] = useState<string | undefined>();
  // Sur petit écran, l'aperçu ne s'ouvre qu'au choix explicite d'un patient.
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    setSelected(new Set());
  }, [resetKey]);

  // Patient ouvert : celui choisi s'il est encore à l'écran ; à défaut, le
  // premier de la page (grand écran seulement).
  const chosen = activeId ? rows.find((r) => r.id === activeId) : undefined;
  const activePatient = chosen ?? (desktop ? rows[0] : undefined);
  const showPreview = view === "desk" && !!activePatient && (desktop || previewOpen);

  const activate = useCallback((id: string) => {
    setActiveId(id);
    setPreviewOpen(true);
  }, []);

  const moveActive = useCallback(
    (delta: 1 | -1) => {
      if (rows.length === 0) return;
      const idx = activePatient ? rows.findIndex((r) => r.id === activePatient.id) : -1;
      const next = Math.min(rows.length - 1, Math.max(0, idx + delta));
      const target = rows[next];
      if (target) setActiveId(target.id);
    },
    [rows, activePatient],
  );

  /** Clic sur une ligne du Tableau (≥ 1024 px) : ouvre le patient au Guichet. */
  const openInDesk = useCallback(
    (id: string) => {
      setActiveId(id);
      setPreviewOpen(true);
      setView("desk");
    },
    [setView],
  );

  return {
    /** Module actif (même en corbeille), pour les retouches d'affichage. */
    templateE,
    enabled,
    canToggle,
    desktop,
    view,
    setView,
    selected,
    setSelected: (next: Set<string>) => setSelected(next),
    clearSelection: () => setSelected(new Set()),
    activePatient,
    /** Patient choisi explicitement (repéré d'un cadre dans le Tableau). */
    chosenId: chosen?.id,
    showPreview,
    activate,
    moveActive,
    openInDesk,
    closePreview: () => setPreviewOpen(false),
    /** Clic Tableau → Guichet, seulement sous E et sur grand écran. */
    rowOpensDesk: enabled && desktop,
  };
}

/**
 * Données de l'aperçu Guichet : résumé du dossier (prochain RDV, dernière
 * consultation, solde si l'utilisateur voit la finance) et allergie critique.
 * Mêmes endpoints et mêmes règles que la fiche patient : le dossier médical
 * n'est lu qu'en clinique générale (ou si la clinique l'a activé).
 */
export function usePatientDeskPreview(id: string | undefined) {
  const showMedicalRecord = useAuthStore((s) => showsGeneralClinical(s.me));
  const summary = usePatientSummary(id);
  const record = usePatientMedicalRecord(showMedicalRecord ? id : undefined);
  const allergy = record.data?.has_critical_allergy ? record.data.allergies : null;
  return { summary, allergy };
}
